import { type ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { type IArrayContainerMetadata } from 'app/extensibility/metadatas/control/array-container-metadata';
import { azureRegionOptions } from 'app/shared/constants/azure-region.const';
import {
    type IDataflowTypeMetadataSink,
    type IDataflowTypeMetadataSource
} from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { TableActionOption } from 'app/model/dataflow/dataset-settings/tabular-type-settings';
import { CosmosDBStartFrom } from 'app/model/dataflow/dataset-settings/cosmos-sql-settings';
import { type IParameterizableMetadata } from 'app/extensibility/metadatas/control/parameterizable-metadata';
import { type IDataflowSchemaTableSelectionMetadata } from 'app/extensibility/metadatas/control/dataflow-schema-table-selection-metadata';

const tableNameDisplayContainerName: string = stringResources.Trigger_EventContainerName;

const tableSelection: IDataflowSchemaTableSelectionMetadata = {
    type: 'dataflow-schema-table-selection',
    bind: 'tableName',
    tableNameLabel: tableNameDisplayContainerName,
    tableNamePlaceholder: stringResources.DataFlowLinkedServiceSelectContainerPlaceholder,
    tableRequired: true,
    selectionType: 'table'
};

const query = (description?: string): IParameterizableMetadata => ({
    type: 'parameterizable',
    child: {
        type: 'textarea',
        bind: 'query',
        label: stringResources.QueryLabel,
        description,
        placeholder: 'select * from c',
        isDataFlowExpression: true,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }]
    },
    dynamicContentTextClass: 'input-spaced',
    showErrorIcon: false,
    isDataFlowExpression: true
});

const changeFeed = (disabled: boolean): ControlMetadata[] => [
    {
        type: 'parameterizable',
        child: {
            disabled,
            type: 'checkbox-switch',
            labelPosition: 'left',
            bind: 'enableChangeFeed',
            label: stringResources.DataFlowChangeFeed,
            description: stringResources.DataFlowChangeFeedDescription,
            escapeDescription: false,
            defaultValue: false,
            isDataFlowExpression: true,
            cases: [
                {
                    caseValue: true,
                    children: [
                        {
                            type: 'parameterizable',
                            isDataFlowExpression: true,
                            attribute: 'changeFeedStartFromBeginning',
                            child: {
                                disabled,
                                type: 'checkbox',
                                bind: 'changeFeedStartFromBeginning',
                                defaultValue: true,
                                isDataFlowExpression: true,
                                label: stringResources.DataFlowChangeFeedStartFromBeginning,
                                description: stringResources.DataFlowChangeFeedStartFromBeginningDescription
                            },
                            dynamicContentTextClass: 'input-spaced',
                            verticalDynamicContentLabel: true
                        }
                    ]
                }
            ]
        },
        isDataFlowExpression: true,
        verticalDynamicContentLabel: true
    },
    {
        type: 'parameterizable',
        child: {
            disabled,
            type: 'checkbox',
            bind: 'systemColumns',
            label: stringResources.SystemColumnsLabel,
            defaultValue: false,
            isDataFlowExpression: true,
            labelPosition: 'left'
        },
        expectedReturnType: 'boolean',
        isDataFlowExpression: true,
        verticalDynamicContentLabel: true
    }
];

const batchSizeParameterized = (label: string): IParameterizableMetadata => ({
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'batchSize',
        inputType: 'number',
        label,
        description: stringResources.DataFlow_BatchSize_Description,
        isDataFlowExpression: true
    },
    dynamicContentTextClass: 'input-spaced',
    showErrorIcon: false,
    isDataFlowExpression: true
});

const preferredRegions: IArrayContainerMetadata = {
    type: 'array-container',
    bind: 'preferredRegions',
    label: stringResources.ModelName_Preferred_Regions,
    description: stringResources.ModelName_Preferred_Regions_Description,
    isLabelVertical: false,
    expandable: false,
    child: {
        type: 'array-item',
        children: [
            {
                type: 'select',
                bind: 'itemPropertyNamePlaceholder',
                placeholderText: stringResources.ModelName_Preferred_Regions_Placeholder,
                options: azureRegionOptions
            }
        ]
    }
};

const partitionKey: ControlMetadata = {
    type: 'textbox',
    bind: 'partitionKey',
    label: stringResources.PartitionKeyLabel,
    description: stringResources.PartitionKeyDescription,
    placeholder: stringResources.PartitionKeyPlaceholder,
    isDataFlowExpression: true
};

const throughput: ControlMetadata = {
    type: 'textbox',
    inputType: 'number',
    bind: 'throughput',
    label: stringResources.ThroughputLabel,
    description: stringResources.ThroughputDescription,
    isDataFlowExpression: true
};

const writeThroughputBudgetLabelParamd: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        inputType: 'number',
        bind: 'writeThroughPutBudget',
        label: stringResources.WriteThroughPutBudgetLabel,
        description: stringResources.WriteThroughPutBudgetDescription,
        isDataFlowExpression: true
    },
    dynamicContentTextClass: 'input-spaced',
    showErrorIcon: false,
    isDataFlowExpression: true
};

/**
 * Cosmos DB MDF inline mode has two store type, `Transactional` and `Analytical`.
 * There are controls for `Transactional` type.
 */
const transactionalControls: ControlMetadata[] = [
    {
        type: 'radio-switch',
        bind: 'useQuery',
        label: stringResources.InputLabel,
        options: [
            {
                label: stringResources.ModelName_Container,
                value: false
            },
            {
                label: stringResources.QueryLabel,
                value: true
            }
        ],
        cases: [
            {
                caseValue: true,
                children: [tableSelection, query(), ...changeFeed(true)]
            },
            {
                caseValue: false,
                children: [tableSelection, ...changeFeed(false)]
            }
        ]
    },
    batchSizeParameterized(stringResources.ModelName_BatchSize),
    {
        type: 'parameterizable',
        child: throughput,
        dynamicContentTextClass: 'input-spaced',
        showErrorIcon: false,
        isDataFlowExpression: true
    },
    preferredRegions
];

// Controls for `Analytical` type.
const analyticalControls: ControlMetadata[] = [
    {
        type: 'radio-switch',
        bind: 'useQuery',
        label: stringResources.InputLabel,
        options: [
            {
                label: stringResources.ModelName_Container,
                value: false
            },
            {
                label: stringResources.QueryLabel,
                value: true
            }
        ],
        cases: [
            {
                caseValue: false,
                children: [tableSelection]
            },
            {
                caseValue: true,
                children: [tableSelection, query(stringResources.CosmosASQueryDescription)]
            }
        ]
    },
    {
        type: 'checkbox',
        defaultValue: true,
        disabled: true,
        label: stringResources.ADFDataMapperChangeDataCaptureLabel,
        description: stringResources.DataFlowCosmosASChangeFeedDescription
    },
    {
        type: 'select-switch',
        label: stringResources.DataFlowStartFrom,
        bind: 'startFrom',
        options: [
            { label: stringResources.Beginning, value: CosmosDBStartFrom.Beginning },
            { label: stringResources.DataFlowColumnType_Timestamp, value: CosmosDBStartFrom.Timestamp },
            { label: stringResources.Now, value: CosmosDBStartFrom.Now }
        ],
        cases: [
            {
                caseValue: CosmosDBStartFrom.Timestamp,
                children: [
                    {
                        type: 'date-time-picker',
                        bind: 'changeFeedStartTimestamp',
                        label: stringResources.DataFlowColumnType_Timestamp,
                        styleOverrides: { width: '479px' },
                        isDataFlowExpression: true
                    }
                ]
            }
        ]
    },
    {
        type: 'parameterizable',
        child: {
            type: 'checkbox',
            bind: 'captureIntermediateUpdate',
            label: stringResources.ModelName_CaptureIntermediateUpdates,
            description: stringResources.CaptureIntermediateUpdateDescription,
            defaultValue: false,
            isDataFlowExpression: true,
            labelPosition: 'left'
        },
        expectedReturnType: 'boolean',
        isDataFlowExpression: true,
        verticalDynamicContentLabel: true
    },
    {
        type: 'parameterizable',
        child: {
            type: 'checkbox',
            bind: 'captureUserDeletes',
            label: stringResources.ModelName_CaptureDeletes,
            description: stringResources.CaptureDeletesDescription,
            defaultValue: true,
            isDataFlowExpression: true,
            labelPosition: 'left'
        },
        expectedReturnType: 'boolean',
        isDataFlowExpression: true,
        verticalDynamicContentLabel: true
    },
    {
        type: 'parameterizable',
        child: {
            type: 'checkbox',
            bind: 'captureTxnTTLDeletes',
            label: stringResources.ModelName_CaptureTransactionalStoreTTLs,
            description: stringResources.CaptureTransactionalStoreTTLsDescription,
            defaultValue: true,
            isDataFlowExpression: true,
            labelPosition: 'left'
        },
        expectedReturnType: 'boolean',
        isDataFlowExpression: true,
        verticalDynamicContentLabel: true
    },
    {
        type: 'parameterizable',
        child: {
            type: 'checkbox',
            bind: 'systemColumns',
            label: stringResources.SystemColumnsLabel,
            defaultValue: false,
            isDataFlowExpression: true,
            labelPosition: 'left'
        },
        expectedReturnType: 'boolean',
        isDataFlowExpression: true,
        verticalDynamicContentLabel: true
    },
    batchSizeParameterized(stringResources.ModelName_BatchSizeInBytes),
    preferredRegions,
    {
        type: 'dataflow-additional-configs',
        bind: 'additionalConfigs',
        description: stringResources.CosmosASAdditionConfigsDescription
    }
];

export const dataflowCosmosDbSource: IDataflowTypeMetadataSource = {
    type: DatasetFormatType.CosmosDB,
    settings: adfModelTypeMap.cosmosSql.dataflowSettings,
    controls: [
              {
                  type: 'logic-switch',
                  bind: 'storeType',
                  defaultValue: 'oltp',
                  allowNull: false,
                  cases: [
                      {
                          caseValue: 'oltp',
                          children: transactionalControls
                      },
                      {
                          caseValue: 'olap',
                          children: analyticalControls
                      }
                  ]
              }
          ] as ControlMetadata[]
};

export const dataflowCosmosDbSink: IDataflowTypeMetadataSink = {
    type: DatasetFormatType.CosmosDB,
    settings: adfModelTypeMap.cosmosSql.dataflowSettings,
    enableAlterRow: true,
    controls: [
        tableSelection,
        {
            type: 'app-dynamic-dataflow-update-method',
            bind: {
                allowInsert: 'allowInsert',
                allowDelete: 'allowDelete',
                allowUpsert: 'allowUpsert',
                allowUpdate: 'allowUpdate'
            }
        },
        {
            type: 'radio-switch',
            label: stringResources.DataFlowContainerAction,
            description: stringResources.DataFlowTableActionDescription,
            options: [
                {
                    label: stringResources.None,
                    value: TableActionOption.None
                },
                {
                    label: stringResources.DataFlowRecreateContainer,
                    value: TableActionOption.Recreate,
                    description: stringResources.DataFlowRecreateTableDescription
                }
            ],
            bind: 'tableActionOption',
            defaultValue: TableActionOption.None,
            cases: [
                {
                    caseValue: TableActionOption.None,
                    children: [
                        batchSizeParameterized(stringResources.ModelName_BatchSize),
                        {
                            type: 'parameterizable',
                            child: partitionKey,
                            dynamicContentTextClass: 'input-spaced',
                            showErrorIcon: false,
                            isDataFlowExpression: true
                        },
                        {
                            type: 'parameterizable',
                            child: throughput,
                            dynamicContentTextClass: 'input-spaced',
                            showErrorIcon: false,
                            isDataFlowExpression: true
                        },
                        writeThroughputBudgetLabelParamd
                    ]
                },
                {
                    caseValue: TableActionOption.Recreate,
                    children: [
                        batchSizeParameterized(stringResources.ModelName_BatchSize),
                        {
                            type: 'parameterizable',
                            child: {
                                ...partitionKey,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            },
                            dynamicContentTextClass: 'input-spaced',
                            showErrorIcon: false,
                            isDataFlowExpression: true
                        },
                        {
                            type: 'parameterizable',
                            child: {
                                ...throughput,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            },
                            dynamicContentTextClass: 'input-spaced',
                            showErrorIcon: false,
                            isDataFlowExpression: true
                        },
                        writeThroughputBudgetLabelParamd
                    ]
                }
            ]
        }
    ]
};

export const dataflowCosmosDbMongoSource: IDataflowTypeMetadataSource = {
    type: DatasetFormatType.CosmosDBMongo,
    settings: adfModelTypeMap.cosmosMongo.dataflowSettings,
    controls: //GlobalVariables.enableCosmosDBSQLASDataFlow
        ([
              {
                  type: 'logic-switch',
                  bind: 'storeType',
                  defaultValue: 'olap',
                  allowNull: false,
                  cases: [
                      {
                          caseValue: 'oltp',
                          children: transactionalControls
                      },
                      {
                          caseValue: 'olap',
                          children: analyticalControls
                      }
                  ]
              }
          ] as ControlMetadata[])
};
